import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, TextField, Button } from '@mui/material';
import SpeechButton from '../../common/SpeechButton';
import LearningMaterialContent from '../../LearningMaterial/LearningMaterialContent';
import { simplifyMaterial } from '../../../services/aiExerciseService'; // Import the simplification service
import { htmlToPlainText } from '../../Exercise/materialConfigUtils';
import { LearningMaterial } from '../../../services/learningMaterialService';
import QuestionProgressControls from './QuestionProgressControls';
interface QuestionStudentProps {
  questionId: string;
  questionNumber: number;
  questionText: string;
  answer: string;
  onAnswerChange: (questionId: string, answer: string) => void;
  feedback: { grade: number; feedback: string; score: number } | null;
  disabled: boolean;
  voiceCode?: string;
}

const QuestionStudent: React.FC<QuestionStudentProps> = ({
  questionId,
  questionNumber,
  questionText,
  answer,
  onAnswerChange,
  feedback,
  disabled,
  voiceCode,
}) => {
  const handleAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onAnswerChange(questionId, e.target.value);
  };

  const pastelColors = ['#f8d7da', '#ffe5b4', '#fff3cd', '#d4edda', '#c3e6cb', '#b9f6ca'];
  const feedbackStyle = feedback ? { backgroundColor: pastelColors[feedback.grade] } : {};

  return (
    <Box mb={2} p={2} border={1} borderRadius={1}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Kysymys {questionNumber}
      </Typography>
      <Box display="flex" alignItems="center">
        <Typography variant="body1" flexGrow={1}>
          {questionText}
        </Typography>
        <SpeechButton id={questionId} text={questionText} voiceCode={voiceCode} />
      </Box>
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        margin="dense"
        value={answer}
        onChange={handleAnswerChange}
        disabled={disabled}
      />
      {feedback && (
        <Box mt={1} p={1} borderRadius={1} style={feedbackStyle}>
          <Typography variant="body2" color="textSecondary">
            Palaute: {feedback.feedback}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Pisteet: {feedback.score}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

interface MaterialBasedStudentProps {
  config: {
    questions: string[];
    voiceCode: string;
    showMaterialToStudent?: boolean;
  };
  linkedMaterial?: {
    id: number;
    title: string;
    content: string;
    sections?: LearningMaterial['sections'];
    materialMedia?: LearningMaterial['materialMedia'];
    showToStudent?: boolean;
  } | null;
  initialAnswers: { [key: string]: string };
  onAnswerChange: (questionId: string, answer: string) => void;
  disabled: boolean;
  assignmentStatus: 'New' | 'Active' | 'InReview';
  hasSubmitted: boolean;
  feedback: { grade: number; feedback: string; score: number }[] | null;
}

const MaterialBasedStudent: React.FC<MaterialBasedStudentProps> = ({
  config,
  linkedMaterial,
  initialAnswers,
  onAnswerChange,
  disabled,
  assignmentStatus,
  hasSubmitted,
  feedback,
}) => {
  const { questions, voiceCode, showMaterialToStudent = false } = config;
  const shouldShowMaterial = linkedMaterial?.showToStudent ?? showMaterialToStudent;
  const materialPlainText = linkedMaterial ? htmlToPlainText(linkedMaterial.content) : '';

  const [simplifiedText, setSimplifiedText] = useState<string | null>(null);
  const [showSimplified, setShowSimplified] = useState(false); // Toggle state
  const [answers, setAnswers] = useState<{ [key: string]: string }>(initialAnswers);
  const [questionViewMode, setQuestionViewMode] = useState<'all' | 'single'>('all');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  useEffect(() => {
    setAnswers(initialAnswers);
  }, [initialAnswers]);

  // Fetch simplified text when the component loads
  useEffect(() => {
    const fetchSimplifiedText = async () => {
      try {
        const plainText = materialPlainText;
        if (!plainText) {
          setSimplifiedText(null);
          return;
        }
        const response = await simplifyMaterial(plainText); // Call the API
        setSimplifiedText(response.data);
      } catch (error) {
        console.error('Error fetching simplified text:', error);
      }
    };

    fetchSimplifiedText();
  }, [materialPlainText]);

  const toggleSimplifiedView = () => {
    setShowSimplified(!showSimplified);
  };

  const handleAnswerChange = (questionId: string, answer: string) => {
    const updatedAnswers = { ...answers, [questionId]: answer };
    setAnswers(updatedAnswers);
    onAnswerChange(questionId, answer);
  };

  const transformedQuestions = useMemo(() => {
    return questions.map((text: string, index: number) => ({
      id: index.toString(),
      text,
      index,
    }));
  }, [questions]);

  useEffect(() => {
    setCurrentQuestionIndex((previousIndex) => {
      if (transformedQuestions.length === 0) {
        return 0;
      }

      return Math.min(previousIndex, transformedQuestions.length - 1);
    });
  }, [transformedQuestions.length]);

  const answeredFlags = useMemo(
    () => transformedQuestions.map((question) => Boolean((answers[question.id] || '').trim())),
    [answers, transformedQuestions],
  );

  const visibleQuestions = questionViewMode === 'single'
    ? transformedQuestions.slice(currentQuestionIndex, currentQuestionIndex + 1)
    : transformedQuestions;

  return (
    <Box>
      {/* Display Material with Rich Text Editor - only if showMaterialToStudent is true */}
      {shouldShowMaterial && linkedMaterial && (
        <Box mb={3} p={2} border={1} borderColor="lightblue" borderRadius={1} bgcolor="lightblue">
          <Typography variant="h6">
            {showSimplified ? 'Yksinkertaistettu materiaali' : linkedMaterial.title}
          </Typography>
          {showSimplified && simplifiedText ? (
            <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
              {simplifiedText}
            </Typography>
          ) : (
            <LearningMaterialContent
              material={linkedMaterial as any}
              defaultView="sectioned"
              allowViewToggle={true}
              allowHide={true}
            />
          )}
          <Button variant="contained" onClick={toggleSimplifiedView} sx={{ mt: 2 }}>
            {showSimplified ? 'Näytä alkuperäinen teksti' : 'Näytä yksinkertaistettu teksti'}
          </Button>
          <SpeechButton 
            id="material-speech" 
            text={showSimplified && simplifiedText ? simplifiedText : materialPlainText} 
            voiceCode={voiceCode} 
          />
        </Box>
      )}


      {/* Display Questions and Feedback */}
      <Box>
        <Typography variant="h6">Kysymykset ja vastaukset</Typography>
        <QuestionProgressControls
          totalQuestions={transformedQuestions.length}
          answeredFlags={answeredFlags}
          currentQuestionIndex={currentQuestionIndex}
          viewMode={questionViewMode}
          onViewModeChange={setQuestionViewMode}
          onPrevious={() => setCurrentQuestionIndex((currentValue) => Math.max(0, currentValue - 1))}
          onNext={() => setCurrentQuestionIndex((currentValue) => Math.min(transformedQuestions.length - 1, currentValue + 1))}
        />
        {visibleQuestions.map(({ id, text, index }) => (
          <QuestionStudent
            key={id}
            questionId={id}
            questionNumber={index + 1}
            questionText={text}
            answer={answers[id] || ''}
            onAnswerChange={handleAnswerChange}
            feedback={feedback ? feedback[index] : null}
            disabled={disabled || hasSubmitted}
            voiceCode={voiceCode}
          />
        ))}
      </Box>
    </Box>
  );
};

export default MaterialBasedStudent;
